import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { chargerEtatOnboarding, majOnboarding } from './api';
import { ecrireOnboarding, lireOnboarding } from './storage';
import { Etape, derivarEtapes, indexEtapeCourante } from './useEtapes';
import {
  ETAT_INCONNU,
  EtatOnboarding,
  LOCAL_PAR_DEFAUT,
  OnboardingLocal,
  VisaMemorise,
} from './types';

interface OnboardingContexte {
  etat: EtatOnboarding;
  local: OnboardingLocal;
  etapes: Etape[];
  nbTerminees: number;
  indexCourant: number;
  doitAfficherCarte: boolean;
  rafraichir: () => void;
  memoriserVisa: (visa: Omit<VisaMemorise, 'le'>) => void;
  masquer: () => void;
  replier: (repliee: boolean) => void;
  /** Célébration en cours, ou null. Pilotée par le provider et NON par
   *  location.state : elle doit pouvoir s'afficher sur un écran de formulaire,
   *  où MyTabs n'est pas monté. */
  celebration: 'voyageur' | 'demande' | null;
  /** Ne déclenche que si l'étape n'a jamais été célébrée pour ce compte. */
  declencherCelebration: (quoi: 'voyageur' | 'demande') => void;
  fermerCelebration: () => void;
}

// Pas de valeur par défaut no-op ici, contrairement à SnackbarProvider : un composant
// monté hors du provider afficherait silencieusement une feuille de route vide.
const Contexte = createContext<OnboardingContexte | null>(null);

export const useOnboarding = (): OnboardingContexte => {
  const ctx = useContext(Contexte);
  if (!ctx) throw new Error('useOnboarding doit être utilisé dans <OnboardingProvider>.');
  return ctx;
};

const lireIdUtilisateur = (): number | null => {
  try {
    const brut = localStorage.getItem('user');
    if (!brut) return null;
    const u = JSON.parse(brut);
    const id = Number(u?.id_utilisateur);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch (e) {
    return null;
  }
};

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();

  const [idUtilisateur, setIdUtilisateur] = useState<number | null>(lireIdUtilisateur);
  const [etat, setEtat] = useState<EtatOnboarding>({ ...ETAT_INCONNU, statut: 'chargement' });
  const [local, setLocal] = useState<OnboardingLocal>(LOCAL_PAR_DEFAUT);
  const [compteurRechargement, setCompteurRechargement] = useState(0);
  const [celebration, setCelebration] = useState<'voyageur' | 'demande' | null>(null);

  const rafraichir = useCallback(() => setCompteurRechargement((c) => c + 1), []);

  // L'identifiant est resynchronisé à chaque changement de route : c'est ainsi que
  // le provider voit une connexion ou une déconnexion, sans écouter localStorage.
  useEffect(() => {
    const id = lireIdUtilisateur();
    setIdUtilisateur((precedent) => (precedent === id ? precedent : id));
  }, [location.pathname]);

  // Rechargement en REVENANT sur /retour — après une création de voyageur ou la
  // validation d'une demande. Pas au premier montage : l'effet ci-dessous s'en charge.
  const cheminPrecedent = useRef<string | null>(null);
  useEffect(() => {
    const precedent = cheminPrecedent.current;
    cheminPrecedent.current = location.pathname;
    if (precedent !== null && precedent !== location.pathname && location.pathname === '/retour') {
      rafraichir();
    }
  }, [location.pathname, rafraichir]);

  useEffect(() => {
    if (idUtilisateur === null) {
      setEtat({ ...ETAT_INCONNU });
      setLocal(LOCAL_PAR_DEFAUT);
      return;
    }

    setLocal(lireOnboarding(idUtilisateur));

    let annule = false;
    setEtat((e) => ({ ...e, statut: 'chargement' }));
    chargerEtatOnboarding(idUtilisateur)
      .then((resultat) => {
        if (!annule) setEtat(resultat);
      })
      .catch(() => {
        // Statut 'inconnu' : la carte ne s'affiche pas. Mieux vaut ne rien montrer
        // que relancer l'onboarding à un client qui a déjà cinq voyageurs.
        if (!annule) setEtat({ ...ETAT_INCONNU });
      });

    return () => {
      annule = true;
    };
  }, [idUtilisateur, compteurRechargement]);

  const memoriserVisa = useCallback(
    (visa: Omit<VisaMemorise, 'le'>) => {
      if (idUtilisateur === null) return;
      setLocal(
        ecrireOnboarding(idUtilisateur, {
          visaChoisi: { ...visa, le: new Date().toISOString() },
        })
      );
    },
    [idUtilisateur]
  );

  const replier = useCallback(
    (repliee: boolean) => {
      if (idUtilisateur === null) return;
      setLocal(ecrireOnboarding(idUtilisateur, { carteRepliee: repliee }));
    },
    [idUtilisateur]
  );

  const masquer = useCallback(() => {
    if (idUtilisateur === null) return;
    setLocal(ecrireOnboarding(idUtilisateur, { masqueeLe: new Date().toISOString() }));
    setEtat((e) => ({ ...e, onboardingMasque: true }));
    // Le choix doit valoir sur TOUS les appareils : sans la colonne en base, une
    // carte congédiée sur le téléphone réapparaîtrait sur l'ordinateur.
    majOnboarding(idUtilisateur, { masquer: true }).catch(() => {
      // L'endpoint n'existe pas encore, ou le réseau est coupé : le masquage local
      // s'applique quand même, il sera simplement rejoué au prochain appareil.
    });
  }, [idUtilisateur]);

  const declencherCelebration = useCallback(
    (quoi: 'voyageur' | 'demande') => {
      if (idUtilisateur === null) return;
      const courant = lireOnboarding(idUtilisateur);
      if (courant.celebrations[quoi]) return;
      // Le verrou est écrit de façon SYNCHRONE, avant tout setState : sous
      // React.StrictMode l'appelant peut passer deux fois, et un verrou porté
      // par le state arriverait trop tard.
      setLocal(
        ecrireOnboarding(idUtilisateur, {
          celebrations: { ...courant.celebrations, [quoi]: true },
        })
      );
      setCelebration(quoi);
    },
    [idUtilisateur]
  );

  const fermerCelebration = useCallback(() => setCelebration(null), []);

  const etapes = derivarEtapes(etat, local);
  const nbTerminees = etapes.filter((e) => e.terminee).length;
  const indexCourant = indexEtapeCourante(etapes);

  const doitAfficherCarte =
    etat.statut === 'ok' &&
    !etat.onboardingTermineLe &&
    !etat.onboardingMasque &&
    local.masqueeLe === null &&
    etat.nbDemandes === 0;

  return (
    <Contexte.Provider
      value={{
        etat,
        local,
        etapes,
        nbTerminees,
        indexCourant,
        doitAfficherCarte,
        rafraichir,
        memoriserVisa,
        masquer,
        replier,
        celebration,
        declencherCelebration,
        fermerCelebration,
      }}
    >
      {children}
    </Contexte.Provider>
  );
};
